import { Box, Collapse, TableCell } from "@mui/material";
import { DotLegalAlert, DotLegalCheckbox, DotLegalDialog, DotLegalEmptyState, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useTranslation } from "@localization/useTranslation";
import TableRow from "@common/components/tableRow/TableRow";
import { EntityType } from "@common/Common.types";
import EmptyStateIcon from "@common/svgs/emptyState.svg?react";
import { useEntityRiskAreasDialog } from "./EntityRiskAreasDialog.hooks";

export interface IEntityRiskAreasDialog {
    entityType: EntityType;
    entityId: string;
    onSaved?: () => void;
    onClose: () => void;
}

interface RiskAreaRow {
    riskAreaId: string;
    name: string;
    isSelected: boolean;
    isBeingRemoved: boolean;
    deleteAssessments: boolean;
}

function EntityRiskAreasDialog(props: IEntityRiskAreasDialog) {
    const { translateString } = useTranslation();
    const { isLoading, rows, hasRiskAreas, hasRemovals, toggleRiskArea, toggleDeleteAssessments, removedRiskAreaNames, onSaveClick, isSaving } =
        useEntityRiskAreasDialog(props);

    const headers: Array<ITableHeader<RiskAreaRow>> = [
        { property: "isSelected", text: "", align: "left", notSortable: true, width: "48px" },
        { property: "name", text: translateString("name"), align: "left", showOnMobile: true },
        // The delete-assessments column only appears once at least one existing link is being removed.
        ...(hasRemovals
            ? [
                  {
                      property: "deleteAssessments" as const,
                      text: translateString("removeEntityDeleteRiskAssessments"),
                      align: "left" as const,
                      notSortable: true,
                      width: "260px",
                  },
              ]
            : []),
    ];

    return (
        <DotLegalDialog
            open
            header={translateString("manageRiskAreas")}
            buttonOkText={translateString("save")}
            secondaryButtonText={translateString("cancel")}
            onOkClick={onSaveClick}
            onSecondaryButtonClick={props.onClose}
            onDialogClose={props.onClose}
            inProgress={isSaving}
            size="md"
        >
            {!isLoading && !hasRiskAreas ? (
                <DotLegalEmptyState icon={<EmptyStateIcon />} text={translateString("noRiskAreas")} />
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Collapse in={removedRiskAreaNames.length > 0}>
                        <DotLegalAlert
                            severity="warning"
                            warningHeader={translateString("warning")}
                            warningText={<Trans i18nKey="removeFromRiskAreaWarning" values={{ names: removedRiskAreaNames.join(", ") }} />}
                            marginBottom={2}
                        />
                    </Collapse>

                    <DotLegalTable
                        headers={headers}
                        data={rows}
                        isLoading={isLoading}
                        defaultOrderBy="name"
                        defaultOrder="asc"
                        emptyText={translateString("noRiskAreas")}
                        paginationLabelOf={translateString("labelOf")}
                        clickableRows
                        renderRow={(row) => (
                            <TableRow key={row.riskAreaId} sx={{ cursor: "pointer" }} onClick={() => toggleRiskArea(row.riskAreaId)}>
                                <TableCell padding="checkbox">
                                    <DotLegalCheckbox checked={row.isSelected} />
                                </TableCell>
                                <TableCell>{row.name}</TableCell>
                                {hasRemovals && (
                                    <TableCell width="260px" onClick={(e) => e.stopPropagation()}>
                                        {row.isBeingRemoved && (
                                            <DotLegalCheckbox
                                                margin={{ marginLeft: -1.5 }}
                                                checked={row.deleteAssessments}
                                                onChange={() => toggleDeleteAssessments(row.riskAreaId)}
                                            />
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        )}
                    />
                </Box>
            )}
        </DotLegalDialog>
    );
}

export default EntityRiskAreasDialog;
